/**
 * CorrelationHeatmap — Pearson r matrix for parameter pairs.
 *
 * Pack widget composed from lib/ui primitives. Industry-agnostic widget
 * shape — the foundry pack just supplies the parameter list + labels.
 *
 * Source: ported from
 *   D:\WORK\SAHAS\Projects\Digital Twin Concept\app\src\components\correlation\CorrelationView.tsx
 * Adapted to DivineIoT's typed widget contract (B0.4).
 */

import { useMemo } from "react";
import { paramNameWords } from "@/lib/brief/param-words";
import { cn } from "@/lib/ui/cn";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/lib/ui/tooltip";

export interface CorrelationCell {
  paramA: string;
  paramB: string;
  /** Pearson r ∈ [-1, 1]. */
  r: number;
  /** Optional p-value (rendered in tooltip). */
  pValue?: number;
  /** Sample size that produced this r. */
  n?: number;
}

export interface CorrelationHeatmapProps {
  /** Ordered list of parameter ids. Drives both row + column order. */
  parameters: string[];
  /** Display labels keyed by parameter id. Falls back to id with underscores → spaces. */
  labels?: Record<string, string>;
  /** Cells — sparse is fine; missing pairs render as r=0. */
  cells: CorrelationCell[];
  /** Currently-selected pair (drives highlight ring). */
  selected?: { paramA: string; paramB: string } | null;
  /** Click handler — emits the (rowParam, colParam) pair. */
  onSelectPair?: (paramA: string, paramB: string) => void;
  className?: string;
}

export function CorrelationHeatmap({
  parameters,
  labels = {},
  cells,
  selected,
  onSelectPair,
  className,
}: CorrelationHeatmapProps) {
  const cellMap = useMemo(() => {
    const m = new Map<string, CorrelationCell>();
    for (const c of cells) {
      m.set(`${c.paramA}|${c.paramB}`, c);
      m.set(`${c.paramB}|${c.paramA}`, c);
    }
    return m;
  }, [cells]);

  const label = (p: string) => labels[p] ?? paramNameWords(p);

  return (
    <TooltipProvider delayDuration={250}>
      <div className={cn("overflow-auto", className)}>
        <table className="border-separate border-spacing-0.5">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-surface-elevated p-1.5"></th>
              {parameters.map((p) => (
                <th
                  key={p}
                  className="h-32 min-w-12 max-w-12 align-bottom"
                >
                  <div className="flex h-full w-12 origin-bottom-left -rotate-45 items-end justify-start whitespace-nowrap pl-1 pb-1 text-[11px] font-medium text-on-surface-muted">
                    {label(p)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {parameters.map((rowParam) => (
              <tr key={rowParam}>
                <th
                  scope="row"
                  className="sticky left-0 z-10 whitespace-nowrap bg-surface-elevated px-2 py-1 text-right text-[11px] font-medium text-on-surface-muted"
                >
                  {label(rowParam)}
                </th>
                {parameters.map((colParam) => {
                  const cell = cellMap.get(`${rowParam}|${colParam}`);
                  const r = cell?.r ?? (rowParam === colParam ? 1 : 0);
                  const isSelected =
                    selected &&
                    ((selected.paramA === rowParam && selected.paramB === colParam) ||
                      (selected.paramA === colParam && selected.paramB === rowParam));
                  const isDiagonal = rowParam === colParam;
                  return (
                    <td key={colParam} className="p-0">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            disabled={isDiagonal}
                            onClick={() =>
                              onSelectPair && !isDiagonal && onSelectPair(rowParam, colParam)
                            }
                            className={cn(
                              "flex size-12 items-center justify-center rounded text-[10px] font-mono font-semibold transition-all",
                              isDiagonal
                                ? "cursor-default opacity-40"
                                : "cursor-pointer hover:scale-105 hover:ring-1 hover:ring-on-surface",
                              isSelected && "ring-2 ring-brand",
                            )}
                            style={{
                              backgroundColor: rToBg(r),
                              color: Math.abs(r) > 0.5 ? "#fff" : "rgb(var(--color-on-surface))",
                            }}
                          >
                            {isDiagonal ? "—" : r.toFixed(2)}
                          </button>
                        </TooltipTrigger>
                        {!isDiagonal && (
                          <TooltipContent>
                            <div className="flex flex-col gap-0.5 text-xs">
                              <span className="font-semibold">
                                {label(rowParam)} × {label(colParam)}
                              </span>
                              <span>
                                r = <span className="font-mono">{r.toFixed(3)}</span>
                              </span>
                              {cell?.pValue !== undefined && (
                                <span>
                                  p ={" "}
                                  <span className="font-mono">
                                    {cell.pValue < 0.001 ? "<0.001" : cell.pValue.toFixed(3)}
                                  </span>
                                </span>
                              )}
                              {cell?.n !== undefined && (
                                <span className="text-on-surface-muted">
                                  n = {cell.n}
                                </span>
                              )}
                            </div>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-3 flex items-center gap-3 text-[11px] text-on-surface-muted">
          <span>−1</span>
          <div
            className="h-3 w-48 rounded-full"
            style={{
              background:
                "linear-gradient(to right, rgb(59,130,246), rgb(220,220,255), rgb(255,255,255), rgb(255,220,220), rgb(239,68,68))",
            }}
            aria-hidden
          />
          <span>+1</span>
          <span className="ml-2">
            Blue = negative · Red = positive
            {onSelectPair ? " · Click a cell to view the scatter." : ""}
          </span>
        </div>
      </div>
    </TooltipProvider>
  );
}

// ── Defects × process-parameters heatmap ─────────────────────────────
//
// A RECTANGULAR sibling of the (symmetric) CorrelationHeatmap: columns are
// defect types (x), rows are process parameters (y), each cell the Pearson r
// between that defect's per-heat rate and that parameter. Same colour scale +
// tooltip idiom; no diagonal (every cell is a real defect↔param pair). The
// "what's driving my defects?" primary view.

export interface DefectCell {
  defect: string;
  param: string;
  /** Pearson r ∈ [-1, 1]. ABSENT on a suppressed cell: a withheld cell has no coefficient,
   *  and defaulting it to 0 would render "cannot assess" as "assessed, no relationship". */
  r?: number;
  pValue?: number;
  n?: number;
  /** Distinct measured values behind those rows. A lab value carried across many heats
   *  gives nDistinct << n: the rows are copies, not independent observations. */
  nDistinct?: number;
  /** The N the p-value was actually computed from (the independent-measurement count). */
  nEff?: number;
  /** True when nDistinct < n, i.e. values were carried forward across heats. */
  carryForward?: boolean;
  /** W4 — the cell did not clear the support gate, so it renders as a DASH carrying `reason`,
   *  never as a zero and never coloured. "Cannot assess" and "assessed, no relationship" are
   *  different statements, and collapsing them tells a reader a parameter does not matter when
   *  the truth is that nobody could look. */
  suppressed?: boolean;
  reason?: string;
  /** W4 — real but underpowered (8 to 24 paired heats). Shows its coefficient WITH a marker,
   *  because "assessed and weak" is information that hiding the cell would destroy. */
  lowPower?: boolean;
  /** W6 — 95% confidence interval, Fisher z on the EFFECTIVE n. Absent below 4 effective
   *  observations, where there is no usable standard error. An r of 0.55 on 9 observations and
   *  on 300 look identical without this. */
  ciLow?: number;
  ciHigh?: number;
  /** W6 — the parameter varies as much between shifts as within one, so any relationship it
   *  shows is shared with everything else that changes at a shift boundary. */
  shiftConfounded?: boolean;
  shiftVarianceShare?: number;
}

export interface DefectParamHeatmapProps {
  /** Column axis (x) — defect types actually present in the heats. */
  defects: string[];
  /** Row axis (y) — process parameters. */
  params: string[];
  /** Labels keyed by defect id (falls back to id with underscores → spaces). */
  defectLabels?: Record<string, string>;
  /** Labels keyed by parameter id. */
  paramLabels?: Record<string, string>;
  cells: DefectCell[];
  /** Currently-selected (defect, param) cell — drives the highlight ring. */
  selected?: { defect: string; param: string } | null;
  /** Click handler — emits the (defect, param) of the clicked cell. */
  onSelectCell?: (defect: string, param: string) => void;
  className?: string;
}

export function DefectParamHeatmap({
  defects,
  params,
  defectLabels = {},
  paramLabels = {},
  cells,
  selected,
  onSelectCell,
  className,
}: DefectParamHeatmapProps) {
  const cellMap = useMemo(() => {
    const m = new Map<string, DefectCell>();
    for (const c of cells) m.set(`${c.defect}|${c.param}`, c);
    return m;
  }, [cells]);

  const dl = (d: string) => defectLabels[d] ?? d.replace(/_/g, " ");
  const pl = (p: string) => paramLabels[p] ?? paramNameWords(p);

  return (
    <TooltipProvider delayDuration={250}>
      <div className={cn("overflow-auto", className)}>
        <table className="border-separate border-spacing-0.5">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-surface-elevated p-1.5"></th>
              {defects.map((d) => (
                <th key={d} className="h-32 min-w-12 max-w-12 align-bottom">
                  <div className="flex h-full w-12 origin-bottom-left -rotate-45 items-end justify-start whitespace-nowrap pl-1 pb-1 text-[11px] font-medium text-on-surface-muted">
                    {dl(d)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {params.map((rowParam) => (
              <tr key={rowParam}>
                <th
                  scope="row"
                  className="sticky left-0 z-10 whitespace-nowrap bg-surface-elevated px-2 py-1 text-right text-[11px] font-medium text-on-surface-muted"
                >
                  {pl(rowParam)}
                </th>
                {defects.map((d) => {
                  const cell = cellMap.get(`${d}|${rowParam}`);
                  // A missing cell is WITHHELD, not a real zero: the backend omits a
                  // cell whose association is unsupported or not significant (the C3
                  // gate). Render it as a distinct dashed "—" so it never reads as a
                  // measured r=0 ("no association"), which is a different claim from
                  // "not enough data to say".
                  // W4: a cell is withheld either because the server sent nothing for the pair
                  // OR because it sent an explicitly SUPPRESSED cell carrying the reason it
                  // failed the support gate. Both render as a dash; the second one can say why.
                  const withheld = cell === undefined || cell.suppressed === true;
                  const r = cell?.r ?? 0;
                  return (
                    <td key={d} className="p-0">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => onSelectCell?.(d, rowParam)}
                            className={cn(
                              "flex size-12 items-center justify-center rounded text-[10px] font-mono font-semibold transition-all",
                              onSelectCell &&
                                "cursor-pointer hover:scale-105 hover:ring-1 hover:ring-on-surface",
                              withheld &&
                                "border border-dashed border-border text-on-surface-muted",
                              selected &&
                                selected.defect === d &&
                                selected.param === rowParam &&
                                "ring-2 ring-brand",
                            )}
                            style={{
                              backgroundColor: withheld ? "transparent" : rToBg(r),
                              color: withheld
                                ? undefined
                                : Math.abs(r) > 0.5
                                  ? "#fff"
                                  : "rgb(var(--color-on-surface))",
                            }}
                          >
                            {withheld ? "—" : r.toFixed(2)}
                            {/* W4: a real but underpowered cell keeps its coefficient and
                                carries a marker, because "assessed and weak" is a different
                                statement from "cannot assess". */}
                            {!withheld && cell?.lowPower && (
                              <span className="ml-0.5 align-super text-[8px]">*</span>
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="flex flex-col gap-0.5 text-xs">
                            <span className="font-semibold">
                              {dl(d)} × {pl(rowParam)}
                            </span>
                            {withheld ? (
                              <span className="text-on-surface-muted">
                                {/* The server's own reason when it sent one, so the reader
                                    learns WHICH support test failed rather than a generic
                                    line covering six different causes. */}
                                {cell?.reason ??
                                  "withheld — not enough support for a significant association"}
                              </span>
                            ) : (
                              <>
                                <span>
                                  r = <span className="font-mono">{r.toFixed(3)}</span>
                                </span>
                                {cell?.pValue !== undefined && (
                                  <span>
                                    p ={" "}
                                    <span className="font-mono">
                                      {cell.pValue < 0.001
                                        ? "<0.001"
                                        : cell.pValue.toFixed(3)}
                                    </span>
                                  </span>
                                )}
                                {cell?.n !== undefined && (
                                  <span className="text-on-surface-muted">
                                    {cell.carryForward && cell.nDistinct !== undefined
                                      ? `${cell.n} heats · ${cell.nDistinct} measurements`
                                      : `n = ${cell.n}`}
                                  </span>
                                )}
                                {cell?.carryForward && (
                                  <span className="text-on-surface-muted">
                                    values carried across heats, so significance uses
                                    the {cell.nEff ?? cell.nDistinct} independent
                                    measurements, not the row count
                                  </span>
                                )}
                                {/* W6: the interval is what separates a suggestive
                                    coefficient from a settled one. An r of 0.55 on nine
                                    independent observations spans roughly -0.15 to 0.88 and
                                    does not even exclude zero; the same 0.55 on three hundred
                                    is tight. Without this they render identically. */}
                                {cell?.ciLow !== undefined &&
                                  cell?.ciHigh !== undefined && (
                                    <span className="text-on-surface-muted">
                                      95% CI{" "}
                                      <span className="font-mono">
                                        {cell.ciLow.toFixed(2)} to {cell.ciHigh.toFixed(2)}
                                      </span>
                                      {cell.ciLow <= 0 && cell.ciHigh >= 0
                                        ? " — includes zero"
                                        : ""}
                                    </span>
                                  )}
                                {cell?.lowPower && (
                                  <span className="text-on-surface-muted">
                                    * underpowered — real, but on few enough paired heats
                                    that the interval is wide
                                  </span>
                                )}
                                {/* W6: shift confounding. A parameter that barely moves
                                    within a shift and jumps between shifts shares its
                                    relationship with everything else that changes at a shift
                                    boundary, so this is not that parameter's own effect. */}
                                {cell?.shiftConfounded && (
                                  <span className="text-warning">
                                    confounded with shift
                                    {cell.shiftVarianceShare !== undefined
                                      ? ` — ${Math.round(cell.shiftVarianceShare * 100)}% of this parameter's variance is between shifts`
                                      : ""}
                                    , so the effect cannot be separated from everything else
                                    that changes at a shift boundary
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-3 flex items-center gap-3 text-[11px] text-on-surface-muted">
          <span>−1</span>
          <div
            className="h-3 w-48 rounded-full"
            style={{
              background:
                "linear-gradient(to right, rgb(59,130,246), rgb(220,220,255), rgb(255,255,255), rgb(255,220,220), rgb(239,68,68))",
            }}
            aria-hidden
          />
          <span>+1</span>
          <span className="ml-2">
            Red = the defect rises with the parameter · Blue = it falls.
            {onSelectCell ? " Click a cell for detail + alarm." : ""}
          </span>
        </div>
      </div>
    </TooltipProvider>
  );
}

/** Map r ∈ [-1,1] to a background color. */
function rToBg(r: number): string {
  const intensity = Math.round(Math.min(Math.abs(r), 1) * 200);
  if (r > 0) return `rgb(${255}, ${255 - intensity}, ${255 - intensity})`;
  if (r < 0) return `rgb(${255 - intensity}, ${255 - intensity}, ${255})`;
  return "rgb(245, 245, 245)";
}
